import { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { Label } from '@/components/ui/label';
import useMatrizEtapas from '@/hooks/useMatrizEtapas';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const TIPOS_CADERNO = [
  { value: 'exam', label: 'Provas' },
  { value: 'train', label: 'Simulados' },
  { value: 'open', label: 'Exercícios' },
  { value: 'desafio', label: 'Desafio ao vivo' }
];

const DISPONIBILIDADE = [
  { value: undefined, label: 'Todas' },
  { value: 'true', label: 'Inéditas' },
  { value: 'false', label: 'Já usadas' }
];

/**
 * Form reativo (sem submit) com 4-5 dimensões.
 * Parent escuta onChange e re-fetcha o count.
 */
export default function QuestionsFilters({ filters, onChange, taxonomies = [], counts = null, onDescritoresChange }) {
  const client = useClient();
  const [descritores, setDescritores] = useState([]);
  // Etapas vêm do backend (GET /admin/trilha_generations/etapas), não hardcoded.
  const { etapas } = useMatrizEtapas();

  useEffect(() => {
    const materiaIds = filters['taxonomies.id'] || [];
    if (materiaIds.length === 0) {
      setDescritores([]);
      onDescritoresChange?.([]);
      return;
    }
    Promise.all(
      materiaIds.map(id =>
        client
          .fetch(`taxonomies/used_by_questions?parent_id=${id}`)
          .then(r => r?.data || [])
          .catch(() => [])
      )
    ).then(results => {
      const flat = results.flat();
      setDescritores(flat);
      onDescritoresChange?.(flat.map(d => d.id));
    });
  }, [filters['taxonomies.id']]); // eslint-disable-line react-hooks/exhaustive-deps

  // counts (opcional) = facetas por dimensão { ineditas, etapa, usadas_em, taxonomias, descritores }.
  // Ausente (ex.: banco de questões) → chips saem sem número, exatamente como antes.
  const chipCount = (dimension, value) => counts?.[dimension]?.[value];

  const setFilter = (key, value) => {
    const next = { ...filters };
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  const toggleArrayValue = (key, value) => {
    const current = Array.isArray(filters[key]) ? filters[key] : [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    if (key === 'taxonomies.id' && next.length === 0) {
      const cleared = { ...filters };
      delete cleared['taxonomies.id'];
      delete cleared.descritor_ids;
      onChange(cleared);
    } else {
      setFilter(key, next);
    }
  };

  return (
    <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
      <FilterGroup label="Disponibilidade">
        {DISPONIBILIDADE.map(opt => (
          <Chip
            key={String(opt.value)}
            active={filters.ineditas === opt.value}
            count={chipCount('ineditas', opt.value)}
            onClick={() => setFilter('ineditas', opt.value)}
          >
            {opt.label}
          </Chip>
        ))}
      </FilterGroup>

      {/* Com uma única etapa (caso comum em produção) o filtro não agrega. */}
      {etapas.length > 1 && (
        <FilterGroup label="Etapa">
          {etapas.map(e => (
            <Chip
              key={e.etapa}
              active={(filters.etapa || []).includes(e.etapa)}
              count={chipCount('etapa', e.etapa)}
              onClick={() => toggleArrayValue('etapa', e.etapa)}
            >
              {e.label}
            </Chip>
          ))}
        </FilterGroup>
      )}

      <FilterGroup label="Usadas em (tipo de caderno)">
        {TIPOS_CADERNO.map(t => (
          <Chip
            key={t.value}
            active={(filters.usadas_em || []).includes(t.value)}
            count={chipCount('usadas_em', t.value)}
            onClick={() => toggleArrayValue('usadas_em', t.value)}
          >
            {t.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup label="Matéria">
        {taxonomies.map(tax => (
          <Chip
            key={tax.id}
            active={(filters['taxonomies.id'] || []).includes(tax.id)}
            count={chipCount('taxonomias', tax.id)}
            onClick={() => toggleArrayValue('taxonomies.id', tax.id)}
          >
            {tax.name}
          </Chip>
        ))}
      </FilterGroup>

      {descritores.length > 0 && (
        <div className="md:col-span-2">
          <FilterGroup label="Descritor">
            {descritores.map(d => (
              <Chip
                key={d.id}
                active={(filters.descritor_ids || []).includes(d.id)}
                count={chipCount('descritores', d.id)}
                onClick={() => toggleArrayValue('descritor_ids', d.id)}
              >
                {d.name}
              </Chip>
            ))}
          </FilterGroup>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-light">{label}</Label>
      <div className="mt-2.5 flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

/**
 * Filter chip com micro-feedback no press + smooth transition de cor.
 * Tailwind hover: já respeita @media (hover) — não dispara em touch tap.
 */
function Chip({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active}
      className={[
        'inline-flex select-none items-center rounded-full border px-3 py-1 text-xs',
        'transition-[transform,colors,border-color,background-color] duration-200',
        'active:scale-[0.96]',
        active ? 'border-primary bg-primary text-white shadow-[0_1px_2px_rgba(0,0,0,0.06)]' : 'hover:border-primary/40 hover:bg-primary/[0.03] border-neutral-lightest bg-white text-neutral-dark'
      ].join(' ')}
      style={{ transitionTimingFunction: EASE_OUT }}
    >
      {children}
      {typeof count === 'number' && <span className={['ml-1.5 text-[11px] tabular-nums', active ? 'text-white/75' : 'text-neutral-light'].join(' ')}>{count}</span>}
    </button>
  );
}
